import i18n from 'i18next';
import { z } from 'zod';

import { environmentStore } from '../states';

import { reportAppError } from './error.utils';

export enum AnyXLanguage {
  /** English (United States) */
  EN_US = 'en-US',
  /** Japanese; 日本語 */
  JA_JP = 'ja-JP',
  /** Chinese (Simplified); 简体中文 */
  ZH_CN = 'zh-CN',
  /** Chinese (Traditional); 繁體中文 */
  KO_KR = 'ko-KR',
}

export const AnyXLanguageSchema = z.nativeEnum(AnyXLanguage).catch(AnyXLanguage.EN_US);

export class LanguageUtils {
  static readonly DEFAULT: AnyXLanguage = AnyXLanguage.EN_US;

  private static updateCurrentLanguage = () => {
    environmentStore.getState().setCurrentLanguage(LanguageUtils.fromI18nLanguage(i18n.language));
  };

  private static changeLanguageWithFallback = async (
    targetLanguage: AnyXLanguage,
    _originalLanguage?: string
  ) => {
    try {
      await i18n.changeLanguage(targetLanguage);
      LanguageUtils.updateCurrentLanguage();
      const langCode = LanguageUtils.getTwoLettersCode(targetLanguage);
      if (typeof window !== 'undefined' && langCode) {
        document.querySelector('html')?.setAttribute('lang', langCode);
      }
    } catch (error) {
      console.error(`Failed to change language to ${targetLanguage}`, error);
      if (targetLanguage !== LanguageUtils.DEFAULT) {
        // Fallback to default if not already trying default
        await LanguageUtils.changeLanguageWithFallback(LanguageUtils.DEFAULT);
      }
    }
  };

  static asLanguageTranslationKey = (
    language: AnyXLanguage | string | null | undefined = AnyXLanguage.EN_US
  ) => {
    return language?.replace('-', '_').toUpperCase();
  };

  static setLanguage = async (language?: string) => {
    const anyXLanguage = LanguageUtils.fromI18nLanguage(language);
    if (LanguageUtils.isAnyxLanguage(anyXLanguage)) {
      await LanguageUtils.changeLanguageWithFallback(anyXLanguage, language);
    } else {
      console.error(`Invalid language: ${language}, Backup to ${LanguageUtils.DEFAULT}`);
      reportAppError(`Invalid language: ${language}`);
      await LanguageUtils.changeLanguageWithFallback(LanguageUtils.DEFAULT);
    }
  };

  static setUserLocale = (language?: string) => {
    environmentStore.getState().setCurrentLocale(LanguageUtils.fromI18nLanguage(language));
  };

  static getCurrentLocale() {
    return environmentStore.getState().currentLocale;
  }

  static fromI18nLanguage(language?: string): AnyXLanguage {
    if (!language) {
      return LanguageUtils.DEFAULT;
    }

    if (LanguageUtils.isAnyxLanguage(language)) {
      return AnyXLanguageSchema.parse(language);
    }

    const [lang, country] = language.split('_');
    if (lang && country) {
      const convertedLanguage = `${lang.toLowerCase()}-${country}`;
      return AnyXLanguageSchema.parse(convertedLanguage);
    }

    return LanguageUtils.DEFAULT;
  }

  static isAnyxLanguage = (language = ''): language is AnyXLanguage => {
    return (Object.values(AnyXLanguage) as string[]).includes(language);
  };

  static isFirstNameFirst(lg?: AnyXLanguage) {
    if (
      [AnyXLanguage.JA_JP, AnyXLanguage.ZH_CN, AnyXLanguage.KO_KR].some((value) => value === lg)
    ) {
      return false;
    }
    return true;
  }

  private static orderNames<T>(firstName: T, lastName: T, lg: AnyXLanguage): [T, T] {
    return LanguageUtils.isFirstNameFirst(lg) ? [firstName, lastName] : [lastName, firstName];
  }

  static localizeName = (
    firstName = '',
    lastName = '',
    lg = LanguageUtils.getCurrentLanguage()
  ) => {
    const fullNameArr = [firstName, lastName];

    if (fullNameArr.some((name) => !!name)) {
      const [first, last] = LanguageUtils.orderNames(firstName, lastName, lg);
      return [first, last].join(' ');
    }

    return '';
  };

  static localizeNodeName = (
    firstName: React.ReactNode,
    lastName: React.ReactNode,
    lg = LanguageUtils.getCurrentLanguage()
  ) => {
    const [first, last] = LanguageUtils.orderNames(firstName, lastName, lg);
    return (
      <>
        {first}
        {last}
      </>
    );
  };

  static getTwoLettersCode = (language: AnyXLanguage) => language?.split('-')[0]?.toLowerCase();

  static getAvailableLanguages() {
    return environmentStore.getState().languages;
  }

  static getCurrentLanguage() {
    return environmentStore.getState().currentLanguage;
  }
}
